// Stumble icons — ported from design-canon/project/src/components.jsx
// Chunky, hand-drawn-ish, single-stroke. Required for the brand look.

import type { CSSProperties } from 'react'

export type IconName =
  | 'back'
  | 'close'
  | 'pin'
  | 'camera'
  | 'users'
  | 'trophy'
  | 'spark'
  | 'home'
  | 'map'
  | 'book'
  | 'you'
  | 'play'
  | 'undo'
  | 'more'
  | 'share'
  | 'plus'
  | 'check'
  | 'flag'
  | 'beer'
  | 'wine'
  | 'cocktail'
  | 'shot'
  | 'edit'

type Props = { name: IconName; size?: number; color?: string; style?: CSSProperties }

export function Icon({ name, size = 22, color = 'currentColor', style }: Props) {
  const s = size
  const stroke = {
    fill: 'none',
    stroke: color,
    strokeWidth: 2.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  switch (name) {
    case 'back':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M15 5l-7 7 7 7" {...stroke} />
        </svg>
      )
    case 'close':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M6 6l12 12M18 6L6 18" {...stroke} />
        </svg>
      )
    case 'pin':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M12 22s7-7.5 7-13a7 7 0 10-14 0c0 5.5 7 13 7 13z" {...stroke} />
          <circle cx="12" cy="9" r="2.4" {...stroke} />
        </svg>
      )
    case 'camera':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M4 8h3l2-2h6l2 2h3v11H4z" {...stroke} />
          <circle cx="12" cy="13.5" r="3.5" {...stroke} />
        </svg>
      )
    case 'users':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <circle cx="9" cy="9" r="3.4" {...stroke} />
          <circle cx="17" cy="10" r="2.6" {...stroke} />
          <path
            d="M3 19c1-3 3.5-4.5 6-4.5S14 16 15 19M14.5 19c.5-2 2-3 3.5-3s3 1 3.5 3"
            {...stroke}
          />
        </svg>
      )
    case 'trophy':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path
            d="M7 4h10v4a5 5 0 01-10 0V4zM5 4H3v3a3 3 0 003 3M19 4h2v3a3 3 0 01-3 3M10 14h4v3a2 2 0 01-2 2 2 2 0 01-2-2v-3zM8 21h8"
            {...stroke}
          />
        </svg>
      )
    case 'spark':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path
            d="M12 3v6M12 15v6M3 12h6M15 12h6M5.6 5.6l4.2 4.2M14.2 14.2l4.2 4.2M5.6 18.4l4.2-4.2M14.2 9.8l4.2-4.2"
            {...stroke}
          />
        </svg>
      )
    case 'home':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M4 11l8-7 8 7v9h-5v-6h-6v6H4z" {...stroke} />
        </svg>
      )
    case 'map':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2zM9 4v16M15 6v16" {...stroke} />
        </svg>
      )
    case 'book':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M4 5a2 2 0 012-2h13v18H6a2 2 0 01-2-2zM7 3v18" {...stroke} />
        </svg>
      )
    case 'you':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <circle cx="12" cy="9" r="3.6" {...stroke} />
          <path d="M4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5" {...stroke} />
        </svg>
      )
    case 'play':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M7 4l13 8-13 8z" {...stroke} />
        </svg>
      )
    case 'undo':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M9 6L4 11l5 5M4 11h10a6 6 0 016 6v1" {...stroke} />
        </svg>
      )
    case 'more':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <circle cx="5" cy="12" r="1.6" fill={color} />
          <circle cx="12" cy="12" r="1.6" fill={color} />
          <circle cx="19" cy="12" r="1.6" fill={color} />
        </svg>
      )
    case 'share':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M12 4v12M7 9l5-5 5 5M5 14v5a1 1 0 001 1h12a1 1 0 001-1v-5" {...stroke} />
        </svg>
      )
    case 'plus':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M12 5v14M5 12h14" {...stroke} />
        </svg>
      )
    case 'check':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M5 12l5 5L20 7" {...stroke} />
        </svg>
      )
    case 'flag':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M5 3v18M5 4h12l-2 4 2 4H5" {...stroke} />
        </svg>
      )
    case 'beer':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M6 7h10v13H6zM16 9h3v8h-3M6 11h10" {...stroke} />
        </svg>
      )
    case 'wine':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M8 4h8l-1 6a3 3 0 01-6 0zM12 13v6M9 20h6" {...stroke} />
        </svg>
      )
    case 'cocktail':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M5 5h14l-7 8zM12 13v7M8 20h8M16 5l3-2" {...stroke} />
        </svg>
      )
    case 'shot':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M8 7h8l-1 13H9zM8 7c0-2 1.5-3 4-3s4 1 4 3" {...stroke} />
        </svg>
      )
    case 'edit':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" style={style} aria-hidden="true">
          <title>{name}</title>
          <path d="M4 20h4l10-10-4-4L4 16zM14 6l4 4" {...stroke} />
        </svg>
      )
    default:
      return null
  }
}
